// ========== 全站主题配置 ==========
// 所有主题颜色通过 CSS 变量定义, 新增主题只需在此添加配置
// 配色设计原则:
//   1. 浅色/深色两套主题, 所有颜色通过 CSS 变量切换
//   2. 文字对比度必须 ≥ WCAG AA 标准 (4.5:1 普通文本, 3:1 大文本)
//   3. 不要用硬编码灰色, 使用语义化的主题变量
//   4. 各层级语义清晰: primary > secondary > muted 逐级递减

import type { ThemeConfig as AntdThemeConfig } from 'antd';

// 代码高亮 (hljs) 语法色 — 随主题切换, 供 markdown.css 等使用
export interface SyntaxColors {
  base: string;        // 代码默认前景
  keyword: string;     // 关键字/运算符
  string: string;      // 字符串
  comment: string;     // 注释/元信息
  func: string;        // 函数/类/类型/标题 (紫色系)
  number: string;      // 数字/字面量/属性/attr (蓝色系)
  builtin: string;     // 内置函数 (橙色系)
  tag: string;         // 标签/名字 (绿色系)
}

// 主题中定义的 CSS 变量
export interface ThemeColors {
  bgPrimary: string;       // 页面最底层背景
  bgSecondary: string;     // 卡片/容器背景
  bgCard: string;          // 卡片内容区背景 (与 bgSecondary 配合使用)
  bgContainer: string;     // 表/表单等 antd 容器背景（必须不透明，避免半透明叠加发蓝）
  bgHover: string;         // 悬浮态背景
  bgElevated: string;      // 浮层背景 (弹层/气泡/下拉, 比 bgSecondary 更"上浮")
  textPrimary: string;     // 主要文字 (对比度 ≥ 7:1)
  textSecondary: string;   // 次要文字 (对比度 ≥ 4.5:1)
  textMuted: string;       // 辅助/弱化文字 (对比度 ≥ 3:1, 仅用于非关键信息)
  accent: string;          // 主题主色
  accentSecondary: string; // 主题辅助色
  border: string;          // 边框颜色
  success: string;
  warning: string;
  danger: string;
  glow: string;            // 光晕/阴影色
  syntax: SyntaxColors;    // 代码高亮语法色

  // 七色装饰色 (七彩云专属)
  // 用于: Badge标签、难度标识、装饰渐变、头像框、进度指示
  colorRed: string;        // 红 - 错误/危险/WA
  colorOrange: string;     // 橙 - 警告/部分通过/Medium
  colorYellow: string;     // 黄 - 成就/星级/高亮
  colorGreen: string;      // 绿 - 成功/AC/Easy
  colorCyan: string;       // 青 - 信息/提示/Hint
  colorBlue: string;       // 蓝 - 链接/操作/Active
  colorPurple: string;     // 紫 - 创意/特殊/Premium
}

// 主题定义
export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  antdAlgorithm: 'default' | 'dark' | 'compact';
  antdToken?: AntdThemeConfig['token'];
}

// 内置主题
export const themes: ThemeConfig[] = [
  // ---- 暗色主题：深空宇宙（Linear 极简暗色打底） ----
  // 借鉴 Linear：近黑中性底 + 细亮边框 + 文字层级分明 + 辉光克制
  {
    id: 'cosmos',
    name: '深空宇宙',
    description: 'Linear 风格的深邃星空暗色主题',
    colors: {
      // 近黑中性底（原 #06061a 蓝黑是灰感来源，近黑底上彩色更亮）
      bgPrimary: '#0A0A0E',
      bgSecondary: '#141419',
      bgCard: 'rgba(255,255,255,0.03)',
      // 表/表单容器：不透明近黑中性
      bgContainer: '#141419',
      bgHover: 'rgba(255,255,255,0.06)',
      bgElevated: '#1A1A20',
      textPrimary: '#FFFFFF',
      textSecondary: 'rgba(255,255,255,0.85)',
      textMuted: 'rgba(255,255,255,0.62)',
      accent: '#4A8BFF',
      accentSecondary: '#8B5CF6',
      border: '#1F1F24',
      success: '#22c55e',
      warning: '#f59e0b',
      danger: '#ef4444',
      glow: 'rgba(74,139,255,0.08)',
      // 代码高亮 (GitHub Dark 语法配色)
      syntax: {
        base: '#c9d1d9',
        keyword: '#ff7b72',
        string: '#a5d6ff',
        comment: '#8b949e',
        func: '#d2a8ff',
        number: '#79c0ff',
        builtin: '#ffa657',
        tag: '#7ee787',
      },
      // 暗色七色 - 提高亮度以在深色背景上可见
      colorRed: '#ff6b6b',
      colorOrange: '#ffa94d',
      colorYellow: '#ffd93d',
      colorGreen: '#69db7c',
      colorCyan: '#38d9a9',
      colorBlue: '#74c0fc',
      colorPurple: '#b197fc',
    },
    antdAlgorithm: 'dark',
  },
  // ---- 浅色主题：七彩云 Colorful ----
  // 风格: 干净的白（近白底 + 纯白卡片 + 极浅边框，靠阴影分层）
  // 七色: 每色有独立语义, 用于装饰而非干扰
  // 对比度保证（on white）:
  //   textPrimary (#1c1c1e) = 14.8:1 (AAA ✓)
  //   textSecondary (#3d3d42) = 9.6:1  (AAA ✓)
  //   textMuted (#6e6e76)     = 4.8:1  (AA ✓)
  {
    id: 'colorful',
    name: '七彩云 Colorful',
    description: '干净明亮的白色主题',
    colors: {
      // === 基础色 (干净白：近白底 + 纯白卡片 + 浅边框) ===
      bgPrimary: '#FAFBFC',
      bgSecondary: '#FFFFFF',
      bgCard: '#FFFFFF',
      bgContainer: '#FFFFFF',
      bgHover: 'rgba(0,0,0,0.035)',
      bgElevated: '#FFFFFF',
      textPrimary: '#1C1C1E',
      textSecondary: '#3D3D42',
      textMuted: '#6E6E76',
      accent: '#2E86DE',
      accentSecondary: '#6C5CE7',
      border: '#EAEAEF',
      glow: 'rgba(46,134,222,0.08)',

      // === 语义色 ===
      success: '#00B894',   // 翡翠绿 - 通过/AC
      warning: '#E17055',   // 日落橙 - 警告/部分通过
      danger: '#D63031',    // 朱砂红 - 错误/WA

      // === 七色装饰色 (七彩云) ===
      // 用于 Badge、标签、难度标识、装饰元素
      colorRed: '#E74C3C',      // 朱红    - 错误/WA/删除
      colorOrange: '#F39C12',   // 琥珀橙  - 警告/中等难度
      colorYellow: '#F1C40F',   // 金色    - 成就/星级/高亮
      colorGreen: '#2ECC71',    // 翡翠绿  - 通过/简单难度
      colorCyan: '#1ABC9C',     // 碧波青  - 信息/提示/Hint
      colorBlue: '#3498DB',     // 海洋蓝  - 链接/操作/活跃
      colorPurple: '#9B59B6',   // 薰衣草紫 - 创意/特殊/Premium

      // 代码高亮 (GitHub Light 语法配色)
      syntax: {
        base: '#24292e',
        keyword: '#d73a49',
        string: '#032f62',
        comment: '#6a737d',
        func: '#6f42c1',
        number: '#005cc5',
        builtin: '#e36209',
        tag: '#22863a',
      },
    },
    antdAlgorithm: 'default',
  },
];

// 默认主题：七彩云（浅色）。
//
// 新访客（localStorage 无 panda-oj-theme）看到的是七彩云；**已访问过的用户不受影响**——
// zustand persist 在首次访问时就会把当时的默认值 'cosmos' 写进 localStorage，
// 所以老用户仍保持他们已存的主题。首屏不闪黑由 index.html 的内联脚本保证（见该文件注释）。
export const DEFAULT_THEME_ID = 'colorful';

// ---------- 信创内核兼容：颜色 → RGB 通道变量 ----------
// 背景：麒麟系统上的奇安信可信浏览器（Chromium 102）不支持 CSS 颜色混合函数（需 Chrome 111+），
// 而含 var() 的这类声明在该内核上会在 computed-value 期整条失效并把属性变成 unset
// （background 即完全透明），补一条兜底声明也救不回来。
// 因此全站半透明色统一写成 rgba(var(--color-X-rgb), a)：代入后是合法颜色，Chromium 87~151 全支持。
// 注意：颜色值是 rgba() 的键（bg-card / bg-hover / text-secondary / text-muted / glow）
// 额外导出 alpha 变量 --color-X-a，用 rgba(var(--color-X-rgb), calc(var(--color-X-a) * P)) 表达
// 「同色相、alpha = 原 alpha × P」——与旧写法（把该 rgba 色取 P% 再与 transparent 混合）结果一致，
// 差别仅在 8 位 alpha 量化误差（实测 ≤0.5/255）。

/** 解析 #RGB / #RRGGBB / rgb() / rgba()，取出通道与 alpha（解析失败返回 null） */
function parseColorChannels(value: string): { rgb: string; alpha: string } | null {
  const v = value.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
  if (hex) {
    let h = hex[1];
    // #RGB 简写先展开成 #RRGGBB
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { rgb: `${r},${g},${b}`, alpha: '1' };
  }
  const fn = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(v);
  if (fn) {
    return {
      rgb: `${Number(fn[1])},${Number(fn[2])},${Number(fn[3])}`,
      alpha: fn[4] === undefined ? '1' : String(Number(fn[4])),
    };
  }
  return null;
}

/**
 * 由主题色生成通道变量。
 * ⚠ `-a` 必须**每个主题都导出**（本主题该色不透明时就导出 1）：
 *   1) 否则该色在浅色主题下不产出 `-a`，`rgba(var(--x-rgb), calc(var(--x-a) * P))`
 *      会落回 theme.css 里**另一套主题**的兜底 alpha（例如浅色拿到暗色的 0.03），
 *      算出 rgba(255,255,255,0.027) 这种近乎全透明的结果；
 *   2) ThemeProvider 用 setProperty 注入且从不删除，切主题时旧的 `-a` 会残留在 <html> 上。
 * 取不到通道时跳过（不抛异常，也不生成会变成空值的变量）。
 */
function channelVars(name: string, value: string): Record<string, string> {
  const parsed = parseColorChannels(value);
  if (!parsed) return {};
  return { [`${name}-rgb`]: parsed.rgb, [`${name}-a`]: parsed.alpha };
}

// 将主题色转换为 CSS 变量键值对
export function themeToCSSVars(theme: ThemeConfig): Record<string, string> {
  const { colors } = theme;
  // 基础色 → CSS 变量名（键名与 theme.css 的首屏兜底值保持一致）
  const baseColors: Record<string, string> = {
    '--color-bg-primary': colors.bgPrimary,
    '--color-bg-secondary': colors.bgSecondary,
    '--color-bg-card': colors.bgCard,
    '--color-bg-container': colors.bgContainer,
    '--color-bg-hover': colors.bgHover,
    '--color-bg-elevated': colors.bgElevated,
    '--color-text-primary': colors.textPrimary,
    '--color-text-secondary': colors.textSecondary,
    '--color-text-muted': colors.textMuted,
    '--color-accent': colors.accent,
    '--color-accent-secondary': colors.accentSecondary,
    '--color-border': colors.border,
    '--color-success': colors.success,
    '--color-warning': colors.warning,
    '--color-danger': colors.danger,
    '--color-glow': colors.glow,
    // 七色装饰色
    '--color-red': colors.colorRed,
    '--color-orange': colors.colorOrange,
    '--color-yellow': colors.colorYellow,
    '--color-green': colors.colorGreen,
    '--color-cyan': colors.colorCyan,
    '--color-blue': colors.colorBlue,
    '--color-purple': colors.colorPurple,
    // 代码高亮语法色
    '--hljs-base': colors.syntax.base,
    '--hljs-keyword': colors.syntax.keyword,
    '--hljs-string': colors.syntax.string,
    '--hljs-comment': colors.syntax.comment,
    '--hljs-func': colors.syntax.func,
    '--hljs-number': colors.syntax.number,
    '--hljs-builtin': colors.syntax.builtin,
    '--hljs-tag': colors.syntax.tag,
  };
  return {
    ...baseColors,
    // 每个颜色键额外导出一份 r,g,b 通道变量，供 rgba(var(--color-X-rgb), a) 使用
    ...Object.entries(baseColors).reduce<Record<string, string>>(
      (acc, [name, value]) => Object.assign(acc, channelVars(name, value)),
      {},
    ),
  };
}

